import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useApiFetch } from '../../lib/api'
import { HorizontalBars, type BarRow } from '../../components/mcp/analytics/HorizontalBars'
import {
  buildOperationCatalogue,
  matchesOperationQuery,
  operationLabel,
} from '../../lib/mcpAnalytics/operationLabel'
import { TabState, TabNoDataYet } from './TabStates'
import { MCP_ANALYTICS_BASE, buildFilterParams } from './api'
import { resolvePeriod, type AnalyticsFilterState } from './useAnalyticsFilters'
import type {
  MambuConnectionDetail,
  McpAnalyticsInvokeActivityResponse,
} from '../../lib/types'

/* ─── Invoke Activity (MS-702) ───

   Endpoints reached through the `invoke` tool, by frequency. The counts come from
   `rollups/operations`, where a batch invoke of N labels was already exploded into
   N rows at write time — so each endpoint a batch touched counts toward its own
   bar, which is the number an admin is actually asking for.

   The endpoint search is applied **after** aggregation: the bars keep their true
   counts and only the rows shown are narrowed. Filtering before aggregation would
   silently rescale every remaining bar against a new maximum. */

export interface InvokeActivityTabProps {
  connectionKey: string
  filters: AnalyticsFilterState
  filtersKey: unknown[]
  reloadToken: number
}

export function InvokeActivityTab({
  connectionKey,
  filters,
  filtersKey,
  reloadToken,
}: InvokeActivityTabProps) {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()
  const [term, setTerm] = useState('')

  const activityQuery = useQuery({
    queryKey: ['mcp-analytics', 'invoke-activity', filtersKey, reloadToken],
    placeholderData: (prev) => prev,
    queryFn: () => {
      const params = buildFilterParams(filters, resolvePeriod(filters))
      return apiFetch<McpAnalyticsInvokeActivityResponse>(
        `${MCP_ANALYTICS_BASE}/invoke-activity?${params.toString()}`,
      )
    },
  })

  // The operation catalogue supplies the "Category > Description" labels. It is a
  // separate, slow-moving resource, so a failure to load it must not blank the
  // chart: every bar then falls back to its raw endpoint id, which is still the
  // honest answer (MS-702 AC14).
  const catalogueQuery = useQuery({
    queryKey: ['mambu-connection', connectionKey],
    queryFn: () =>
      apiFetch<MambuConnectionDetail>(`/api/v1/mambu-connections/${connectionKey}`),
    staleTime: 5 * 60 * 1000,
  })

  const catalogue = useMemo(
    () => buildOperationCatalogue(catalogueQuery.data?.operations ?? []),
    [catalogueQuery.data],
  )

  const allRows: BarRow[] = useMemo(
    () =>
      (activityQuery.data?.items ?? []).map(item => ({
        key: item.operationKey,
        label: operationLabel(item.operationKey, catalogue),
        value: item.invocations,
      })),
    [activityQuery.data, catalogue],
  )

  // The scale is taken from the UNFILTERED set, so searching narrows the list
  // without redrawing the bars that remain.
  const max = allRows.reduce((highest, row) => Math.max(highest, row.value), 0)
  const rows = allRows.filter(row => matchesOperationQuery(row.key, catalogue, term))

  const trimmedTerm = term.trim()
  const hasFilters =
    filters.users.length > 0 || filters.channels.length > 0 || filters.invocationModes.length > 0

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h3>{t('mcpAnalytics.detail.invoke.title')}</h3>
          <p>{t('mcpAnalytics.detail.invoke.subtitle')}</p>
        </div>
      </div>
      <div className="card-body">
        {/* The endpoint filter spans the card's content width so its edges line up
            with the divider under the card header. The placeholder carries the
            instruction, so no separate label is needed. */}
        <input
          type="text"
          className="form-input mcpobs-ep-search"
          placeholder={t('mcpAnalytics.detail.invoke.searchPlaceholder')}
          aria-label={t('mcpAnalytics.detail.invoke.searchAria')}
          value={term}
          onChange={e => setTerm(e.target.value)}
        />

        <TabState
          isLoading={activityQuery.isLoading}
          isError={activityQuery.isError}
          error={activityQuery.error}
          onRetry={() => void activityQuery.refetch()}
        >
          {rows.length > 0 ? (
            <HorizontalBars rows={rows} max={max} />
          ) : (
            <div style={{ padding: 14, color: 'var(--text-tertiary)', fontSize: 13 }}>
              {/* Three distinct outcomes, never collapsed into one: no match for a
                  term, no invoke calls in this period, and no activity at all
                  (MS-702 AC20). The term is echoed verbatim and its quotation marks
                  come from the translation, so a locale that quotes differently
                  does so correctly (AC20a/20b).

                  `allRows.length === 0` is not re-tested on the term-less branches:
                  `matchesOperationQuery` matches everything on a blank term, so
                  reaching here without one already means there was nothing to
                  filter. */}
              {trimmedTerm ? (
                t('mcpAnalytics.detail.invoke.noMatches', { term: trimmedTerm })
              ) : hasFilters ? (
                t('mcpAnalytics.detail.invoke.empty')
              ) : (
                <TabNoDataYet />
              )}
            </div>
          )}
        </TabState>
      </div>
    </div>
  )
}
